import { useQuery } from "@tanstack/react-query";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "@/base";

interface LicenciadaMasterData {
  id: string;
  razaoSocial: string;
}

/**
 * Hook para buscar o ID e dados da licenciada master da configuração geral
 */
export const useLicenciadaMaster = () => {
  return useQuery({
    queryKey: ["config", "licenciadaMaster"],
    queryFn: async (): Promise<LicenciadaMasterData> => {
      const configReference = doc(firestore, "config", "geral");
      const configSnap = await getDoc(configReference);

      if (!configSnap.exists()) {
        throw new Error("Configuração geral não encontrada");
      }

      const data = configSnap.data();
      const licenciadaMasterReference = data?.licenciadaMaster;

      if (!licenciadaMasterReference) {
        throw new Error("Licenciada master não configurada");
      }

      let licenciadaMasterId: string;

      // Se for uma referência, extrai o ID
      if (typeof licenciadaMasterReference === "object" && licenciadaMasterReference.id) {
        licenciadaMasterId = licenciadaMasterReference.id as string;
      }
      // Se for uma string, pode ser um path ou ID direto
      else if (typeof licenciadaMasterReference === "string") {
        // Se começar com /licenciadas/, extrai apenas o ID
        if (licenciadaMasterReference.startsWith("/licenciadas/")) {
          licenciadaMasterId = licenciadaMasterReference.replace("/licenciadas/", "");
        } else {
          // Se já for um ID, usa direto
          licenciadaMasterId = licenciadaMasterReference;
        }
      } else {
        throw new TypeError("Formato inválido para licenciada master");
      }

      // Busca os dados da licenciada master
      const licenciadaMasterDocument = await getDoc(doc(firestore, "licenciadas", licenciadaMasterId));

      if (!licenciadaMasterDocument.exists()) {
        throw new Error("Licenciada master não encontrada");
      }

      const licenciadaData = licenciadaMasterDocument.data();

      return {
        id: licenciadaMasterId,
        razaoSocial: licenciadaData?.razaoSocial || "Sem razão social"
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
    retry: 2
  });
};
